import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

/**
 * MIPCOM — marché international des contenus, en octobre au Palais des
 * Festivals. Page créée le 25/09/2026 : 🟢 MESURE, MIPCOM était cité dans les
 * trois fichiers de trajets (`app/data/transfers/routes*.ts`) mais n'avait
 * aucune page, alors que MIPIM, son équivalent de mars, apparaissait dans 27
 * fichiers. C'est le prochain grand salon de Cannes, d'où la priorité.
 *
 * ⛔ Aucune date précise n'est écrite ici, volontairement. Le dépôt ne porte
 * aucun calendrier d'édition, et une date inventée se périme en un an sur une
 * page qui, elle, reste. Le gabarit MIPIM dit « en mars » sans plus : on dit
 * « en octobre ». Les seuls chiffres de cette page sont des tarifs réels.
 */
const mipcom: ChauffeurPage = {
  slug: "mipcom",
  title: "Chauffeur privé MIPCOM Cannes – VTC affaires",
  subtitle:
    "Chauffeur privé pendant le MIPCOM : transferts délégations, rendez-vous acheteurs enchaînés et dîners de networking autour du Palais des Festivals.",
  keywords: ["chauffeur mipcom", "vtc mipcom cannes", "chauffeur privé mipcom"],
  hero: {
    title: "Chauffeur privé pendant le MIPCOM",
    subtitle:
      "Marché international des contenus, en octobre à Cannes : un chauffeur d'affaires pour tenir un agenda de rendez-vous back-to-back.",
    keywords: [
      "chauffeur MIPCOM",
      "VTC affaires Cannes",
      "chauffeur Palais des Festivals",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant le MIPCOM",
    badges: [
      "Transferts délégations",
      "Rendez-vous enchaînés",
      "Dîners de networking",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur d'affaires au MIPCOM",
    items: [
      {
        title: "Agenda d'acheteurs tenu",
        description:
          "Stands au Palais, suites d'hôtel privatisées et projections s'enchaînent au quart d'heure : un chauffeur dédié absorbe les aléas sans décaler la journée.",
      },
      {
        title: "Roadshows multi-stops",
        description:
          "Distributeurs, diffuseurs et producteurs reçoivent dans des lieux différents : même véhicule, même chauffeur, un seul fil pour toute la journée.",
      },
      {
        title: "Transferts aéroport business",
        description:
          "Nice Côte d'Azur concentre les arrivées internationales en octobre : suivi de vol, accueil pancarte et liaison directe vers la Croisette. Dès 100 €.",
      },
      {
        title: "Dîners & soirées de marché",
        description:
          "Réceptions de catalogues, dîners de closing et soirées partenaires se terminent tard : le retour de nuit est assuré.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant le MIPCOM",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Liaison Nice ↔ Cannes avec suivi de vol, à partir de 100 €, pendant le pic d'arrivées du marché en octobre.",
      },
      {
        title: "Mise à disposition journée",
        text: "Chauffeur dédié à l'heure ou à la journée, dès 240 € pour trois heures, pour enchaîner rendez-vous au Palais et déjeuners d'affaires.",
      },
      {
        title: "Roadshow acheteurs",
        text: "Itinéraires multi-stops entre le Palais, les suites d'hôtel de la Croisette et les bureaux temporaires des distributeurs.",
      },
      {
        title: "Soirées & dîners de closing",
        text: "Réceptions de networking et dîners de négociation sur la Croisette, avec attente du chauffeur et retour tardif.",
      },
      {
        title: "Navettes délégations",
        text: "Coordination multi-véhicules pour les délégations de chaînes, studios et institutions, du van 7 places au minibus VIP 16 places.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant le MIPCOM",
    items: [
      {
        name: "Palais des Festivals et des Congrès",
        description:
          "Stands, pavillons nationaux, salles de projection et espaces de rendez-vous.",
      },
      {
        name: "La Croisette",
        description:
          "Suites d'hôtel privatisées par les distributeurs et terrasses de réception.",
      },
      {
        name: "Aéroport de Nice Côte d'Azur",
        description:
          "Terminaux 1 et 2, arrivées internationales concentrées en octobre.",
      },
      {
        name: "Mougins & Mandelieu",
        description: "Hébergement des délégations et dîners clients au calme.",
      },
      {
        name: "Monaco",
        description:
          "Rendez-vous et réceptions en Principauté, à 250 € le transfert depuis Cannes.",
      },
    ],
  },
  generic: {
    heading: "Le MIPCOM vu d'un chauffeur d'affaires",
    subheading:
      "En octobre, le marché international des contenus remplit Cannes de rendez-vous d'achat et de vente enchaînés.",
    paragraphs: [
      "Chaque mois d'octobre, le MIPCOM réunit au Palais des Festivals les diffuseurs, distributeurs, studios et plateformes du monde entier. Les rendez-vous se calent au quart d'heure entre les stands, les salles de projection et les suites d'hôtel de la Croisette : un retard de transport coûte une négociation, pas seulement une réunion.",
      "Les arrivées se concentrent à l'aéroport de Nice Côte d'Azur, à 27 km et une trentaine de minutes de Cannes. Le suivi de vol, l'accueil pancarte au terminal et le transfert direct vers l'hôtel évitent la file d'attente au moment précis où toute la profession atterrit. Le trajet est à 100 € en berline Mercedes Classe E, forfait fixé à l'avance.",
      "Au-delà du transfert, la mise à disposition transforme la journée en roadshow continu : plusieurs rendez-vous acheteurs, un déjeuner, une projection et un dîner de closing enchaînés avec le même chauffeur et le même véhicule, à partir de 240 € pour trois heures, jusqu'aux soirées tardives de la Croisette.",
    ],
    bullets: [
      "Roadshow multi-stops",
      "Transfert aéroport dès 100 €",
      "Dîners de closing",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour le MIPCOM",
    steps: [
      "Communiquez vos dates de marché, votre hôtel et la liste de vos rendez-vous.",
      "Nous construisons l'itinéraire multi-stops et calons les horaires sur votre agenda.",
      "Le chauffeur se présente en tenue, avec suivi de vol pour vos arrivées à Nice.",
      "Mise à disposition journée et soirée pour tenir projections, réunions et dîners.",
    ],
  },
  faq: [
    {
      q: "Combien coûte un chauffeur privé pendant le MIPCOM ?",
      a: "Le transfert entre l'aéroport de Nice et Cannes est à 100 € en berline Mercedes Classe E, forfait fixé à l'avance. La mise à disposition avec chauffeur dédié démarre à 240 € pour trois heures dans la même catégorie, et 600 € pour une journée de huit heures. Les vans et minibus VIP pour délégations sont tarifés selon la catégorie.",
    },
    {
      q: "Comment réserver un chauffeur d'affaires pendant le MIPCOM ?",
      a: "Réservez en amont : en octobre, la demande sur Cannes grimpe fortement et les véhicules partent vite. Transmettez vos dates, votre hôtel et vos rendez-vous pour que nous bloquions un chauffeur dédié et préparions un itinéraire multi-stops.",
    },
    {
      q: "Pouvez-vous enchaîner plusieurs rendez-vous acheteurs dans la journée ?",
      a: "Oui, c'est l'usage typique du MIPCOM. La mise à disposition garde le même chauffeur et le même véhicule pour un roadshow complet entre le Palais, les suites d'hôtel et les bureaux temporaires, sans réserver à chaque trajet.",
    },
    {
      q: "Gérez-vous les arrivées à l'aéroport de Nice pendant le marché ?",
      a: "Oui, avec suivi de vol et accueil pancarte au terminal 1 ou 2. En octobre, l'aéroport de Nice Côte d'Azur connaît un pic d'arrivées internationales : nous anticipons et adaptons l'heure de prise en charge en cas de retard, avec 30 minutes de courtoisie à l'arrivée.",
    },
    {
      q: "Transportez-vous des délégations entières ?",
      a: "Oui. Le van Mercedes Classe V accueille 7 passagers et le minibus VIP Mercedes Sprinter jusqu'à 16. Pour les délégations plus larges, nous coordonnons plusieurs véhicules avec des chauffeurs dédiés sur toute la durée du marché.",
    },
  ],
  relatedLinks: [
    {
      label: "Réserver un transfert Cannes Aéroport de Nice",
      href: "/chauffeur/transferts/cannes-aeroport-nice",
    },
    {
      label: "Chauffeur privé MIPIM, le marché de mars",
      href: "/chauffeur/mipim",
    },
    {
      label: "Chauffeur privé pour entreprise",
      href: "/chauffeur/entreprise",
    },
    {
      label: "Vérifier les disponibilités",
      href: "/reservation",
    },
  ],
  cta: {
    title: "Votre chauffeur d'affaires pour le MIPCOM",
    text: "Transferts aéroport dès 100 €, roadshows acheteurs et dîners de closing : réservez un chauffeur dédié pour le marché.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title: "Chauffeur MIPCOM Cannes — VTC affaires dès 100 €",
    description:
      "Chauffeur privé pendant le MIPCOM à Cannes : transfert aéroport de Nice dès 100 €, mise à disposition dès 240 €, roadshows acheteurs et dîners. Réservez.",
    canonical: "/chauffeur/mipcom",
  },
};

export default mipcom;
